/**
 * Shared Sidebar Component
 * Provides consistent sidebar functionality across all portals
 */

import React from 'react';
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  useTheme,
  alpha,
  IconButton,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLayout } from '../LayoutProvider';

/**
 * Sidebar Component
 * @param {Object} props - Component props
 * @param {Array} props.navigationItems - Navigation menu items
 * @param {ReactNode} props.header - Custom sidebar header
 * @param {ReactNode} props.footer - Custom sidebar footer
 * @param {boolean} props.permanent - Whether sidebar is permanent (not overlay)
 * @param {Function} props.onClose - Close handler for mobile overlay
 */
export const Sidebar = ({ navigationItems = [], header, footer, permanent = false, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    sidebarOpen,
    sidebarCollapsed,
    sidebarConfig,
    layoutConfig,
    themeConfig,
    layoutStyles,
    toggleSidebar,
    screenSize,
    isDark,
  } = useLayout();

  const [expandedItems, setExpandedItems] = React.useState(new Set());

  // Handle navigation item click
  const handleItemClick = (item) => {
    if (item.children) {
      // Toggle expansion for items with children
      const newExpanded = new Set(expandedItems);
      if (newExpanded.has(item.key)) {
        newExpanded.delete(item.key);
      } else {
        newExpanded.add(item.key);
      }
      setExpandedItems(newExpanded);
    } else if (item.path) {
      // Navigate to path
      navigate(item.path);

      // Close sidebar if floating after navigation
      if (sidebarConfig.floating && onClose) {
        onClose();
      }
    } else if (item.onClick) {
      // Execute custom click handler
      item.onClick();
    }
  };

  // Check if item is active
  const isItemActive = (item) => {
    if (!item.path) return false;
    return (
      location.pathname === item.path ||
      (item.activePattern && new RegExp(item.activePattern).test(location.pathname))
    );
  };

  // Render navigation item
  const renderNavigationItem = (item, level = 0) => {
    const isActive = isItemActive(item);
    const isExpanded = expandedItems.has(item.key);
    const hasChildren = item.children && item.children.length > 0;

    return (
      <React.Fragment key={item.key}>
        <ListItem
          disablePadding
          sx={{
            display: 'block',
            pl: level * 2,
          }}
        >
          <ListItemButton
            onClick={() => handleItemClick(item)}
            selected={isActive}
            sx={{
              minHeight: screenSize === 'monitor' ? 56 : 48,
              justifyContent: sidebarCollapsed ? 'center' : 'initial',
              px: screenSize === 'monitor' ? 3 : 2.5,
              borderRadius: 2,
              mx: 1,
              mb: 0.5,
              backgroundColor: isActive
                ? alpha(
                    isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
                    isDark ? 0.15 : 0.1
                  )
                : 'transparent',
              '&:hover': {
                backgroundColor: alpha(
                  isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
                  isDark ? 0.1 : 0.05
                ),
                transform: 'translateX(4px)',
              },
              '&.Mui-selected': {
                backgroundColor: alpha(
                  isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
                  isDark ? 0.2 : 0.15
                ),
                '&:hover': {
                  backgroundColor: alpha(
                    isDark ? themeConfig.primaryColorDark : themeConfig.primaryColor,
                    isDark ? 0.25 : 0.2
                  ),
                },
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {item.icon && (
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: sidebarCollapsed ? 'auto' : 3,
                  justifyContent: 'center',
                  color: isActive
                    ? isDark
                      ? themeConfig.primaryColorDark
                      : themeConfig.primaryColor
                    : isDark
                      ? themeConfig.primaryColorDark
                      : 'inherit',
                  transition: 'color 0.2s ease',
                }}
              >
                {item.icon}
              </ListItemIcon>
            )}

            <ListItemText
              primary={item.label}
              sx={{
                opacity: sidebarCollapsed ? 0 : 1,
                color: isActive
                  ? isDark
                    ? themeConfig.primaryColorDark
                    : themeConfig.primaryColor
                  : 'inherit',
                transition: 'opacity 0.3s ease, color 0.2s ease',
                '& .MuiListItemText-primary': {
                  fontWeight: isActive ? 600 : 400,
                  fontSize: screenSize === 'monitor' ? '1.1rem' : '1rem',
                },
              }}
            />

            {hasChildren && !sidebarCollapsed && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
          </ListItemButton>
        </ListItem>

        {hasChildren && !sidebarCollapsed && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  // Sidebar content
  const sidebarContent = (
    <Box
      sx={{
        width: sidebarCollapsed ? sidebarConfig.collapsedWidth : sidebarConfig.width,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ...layoutStyles.glassMorphism,
        borderRight: `1px solid ${isDark ? themeConfig.borderDark : themeConfig.borderLight}`,
        transition: theme.transitions.create(['width', 'background-color'], {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
      }}
    >
      {/* Header */}
      {header && !sidebarCollapsed && (
        <Box
          sx={{
            p: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {header}
        </Box>
      )}

      {/* Collapse Toggle (for permanent sidebar) */}
      {permanent && layoutConfig.sidebarCollapsible && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
          <IconButton onClick={toggleSidebar} size="small">
            {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>
      )}

      {/* Navigation */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List>{navigationItems.map((item) => renderNavigationItem(item))}</List>
      </Box>

      {/* Footer */}
      {footer && !sidebarCollapsed && (
        <Box
          sx={{
            p: 2,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );

  // Render as overlay drawer for floating sidebar
  if (sidebarConfig.overlay) {
    return (
      <Drawer
        variant="temporary"
        anchor="left"
        open={sidebarOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance for overlay
        }}
        sx={{
          '& .MuiDrawer-paper': {
            width: sidebarConfig.width,
            marginTop: `${layoutConfig.headerHeight}px`,
            height: `calc(100% - ${layoutConfig.headerHeight}px)`,
            boxSizing: 'border-box',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Render as permanent drawer for desktop
  return (
    <Drawer
      variant="permanent"
      sx={{
        width: sidebarCollapsed ? sidebarConfig.collapsedWidth : sidebarConfig.width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: sidebarCollapsed ? sidebarConfig.collapsedWidth : sidebarConfig.width,
          marginTop: `${layoutConfig.headerHeight}px`,
          height: `calc(100% - ${layoutConfig.headerHeight}px)`,
          boxSizing: 'border-box',
          transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        },
      }}
      open={sidebarOpen}
    >
      {sidebarContent}
    </Drawer>
  );
};

export default Sidebar;
